import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import type { EnvironmentId, ScopedThreadRef, ThreadId } from "@amoreg/contracts";
import type { DraftId } from "./composerDraftStore";
import { orgUrlSegment } from "./orgRouting";

/**
 * Tenant-first routing: URLs are `/$org/$threadId` (and
 * `/$org/draft/$draftId`), where `org` is the Amoreg organization slug —
 * with the organization id (also the thread's `projectId`, the mirror
 * invariant) accepted as a fallback; see `orgRouting.ts`. The backend
 * environment no longer appears in the URL: the hosted product talks to a
 * single primary environment, so readers derive the `environmentId` from the
 * connection catalog (`usePrimaryEnvironmentId`) instead of the path.
 */

export type ThreadRouteTarget =
  | {
      kind: "server";
      threadRef: ScopedThreadRef;
    }
  | {
      kind: "draft";
      draftId: DraftId;
    };

type DraftThreadRouteState = {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  promotedTo?: ScopedThreadRef | null;
};

export type ThreadRouteRenderState = "loading" | "ready" | "missing";

export function resolveThreadRouteRenderState(input: {
  bootstrapComplete: boolean;
  serverThreadShellExists: boolean;
  serverThreadDetailExists: boolean;
  serverThreadDetailDeleted: boolean;
  draftThreadExists: boolean;
}): ThreadRouteRenderState {
  if (!input.bootstrapComplete) {
    return "loading";
  }
  if (input.serverThreadDetailExists || input.draftThreadExists) {
    return "ready";
  }
  if (input.serverThreadDetailDeleted) {
    return "missing";
  }
  return input.serverThreadShellExists ? "loading" : "missing";
}

/**
 * The org (== the thread's projectId) owns the first URL segment. Callers
 * pass the org/project *id*; the segment renders as the slug when known.
 */
export function buildThreadRouteParams(
  ref: ScopedThreadRef,
  orgId: string,
): {
  org: string;
  threadId: ThreadId;
} {
  return {
    org: orgUrlSegment(orgId),
    threadId: ref.threadId,
  };
}

export function buildDraftThreadRouteParams(
  draftId: DraftId,
  orgId: string,
): {
  org: string;
  draftId: DraftId;
} {
  return { org: orgUrlSegment(orgId), draftId };
}

export function resolveThreadRouteRef(
  params: Partial<Record<"org" | "threadId", string | undefined>>,
  environmentId: EnvironmentId | null,
): ScopedThreadRef | null {
  if (!params.org || !params.threadId || !environmentId) {
    return null;
  }

  return scopeThreadRef(environmentId, params.threadId as ThreadId);
}

export function resolveThreadRouteTarget(
  params: Partial<Record<"org" | "threadId" | "draftId", string | undefined>>,
  environmentId: EnvironmentId | null,
): ThreadRouteTarget | null {
  if (params.org && params.threadId && environmentId) {
    return {
      kind: "server",
      threadRef: scopeThreadRef(environmentId, params.threadId as ThreadId),
    };
  }

  if (!params.draftId) {
    return null;
  }

  return {
    kind: "draft",
    draftId: params.draftId as DraftId,
  };
}

/** The raw first URL segment on org routes — a slug or an id. */
export function resolveActiveOrgSegment(
  params: Partial<Record<"org", string | undefined>>,
): string | null {
  return params.org ?? null;
}

/**
 * Resolves the thread represented by either a canonical thread route or a
 * draft route whose promotion to a server thread has been recorded.
 */
export function resolveActiveThreadRouteRef(
  target: ThreadRouteTarget | null,
  draftThread: DraftThreadRouteState | null,
): ScopedThreadRef | null {
  if (target?.kind === "server") {
    return target.threadRef;
  }
  if (target?.kind !== "draft" || !draftThread?.promotedTo) {
    return null;
  }
  return draftThread.promotedTo;
}
